import axios from 'axios';
import { recordFailedRequest } from '../utils/bugContext';
import {
  clearAccessToken,
  clearActiveMode,
  clearDrugLabAnonToken,
  clearTutorialTestToken,
  getAuthToken,
  getDrugLabAnonToken,
  hasTutorialTestToken,
  isAnonDemoOnly,
  setAccessToken,
  setDrugLabAnonToken,
} from '../utils/authStorage';
import {
  AUTH_FORCED_SIGN_OUT_EVENT,
  clearPrivateSessionStorage,
} from '../utils/authLifecycleCleanup';
import { isPublicPath } from '../utils/publicPaths';
import { readHtnEquipmentSlugs } from '../utils/htnEquipmentPersistence';
import { isNativeShell } from '../utils/originKind';

// The /play/:slug demos whose anon session is a curated, demo-scoped account (the
// backend seeds an HTN region instead of a random loadout). Kept in lockstep with
// backend ANON_DEMO_SLUGS. When an anon 401 re-mint happens inside one of these, the
// fresh account must be minted with the SAME demo_slug (or it gets a generic random
// loadout), and the run's persisted regimen must be replayed onto it.
const HTN_DEMO_SLUGS = new Set(['hypertension', 'finerenone']);
function htnDemoSlugFromPath() {
  try {
    const m = /^\/play\/([^/]+)/.exec(window.location?.pathname || '');
    const slug = m && m[1] ? decodeURIComponent(m[1]).toLowerCase() : '';
    return HTN_DEMO_SLUGS.has(slug) ? slug : null;
  } catch (_) {
    return null;
  }
}

const getApiUrl = () => {
  const DEV_BACKEND_PORT = 7001;
  const PROD_BACKEND_PORT = 7000;
  const PRODUCTION_API = 'https://api.debono.ai';

  try {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined' && window.location) {
      // A packaged native shell must be decided BEFORE any host or port branch
      // below, because its location is not a real origin. Capacitor on iOS
      // serves the bundle from `capacitor://localhost`, whose port is the empty
      // string: it therefore matches the `localhost` branch, misses every rung
      // of that port ladder, falls through to PROD_BACKEND_PORT and returns
      // `http://localhost:7000` — a cleartext request to the DEVICE, which App
      // Transport Security blocks outright. Every API call fails, on device,
      // with no way to configure around it.
      //
      // This is the one branch that SHOULD reach the external domain. The
      // warning further down about REACT_APP_API_URL is about local and
      // headless access from a browser, where the external domain is wrong; a
      // packaged binary has no local backend to reach, so the opposite holds.
      // A staging native build would add its own override here.
      if (isNativeShell()) {
        return PRODUCTION_API;
      }

      const host = window.location.hostname;

      // IMPORTANT: Check IP-based access FIRST (Tailscale, local network)
      // When accessed via IP, browser's "localhost" means the client machine, not the server
      // So we must use the same hostname/IP as the frontend, just different port

      // For Tailscale IPs (100.x.x.x), use same hostname with backend port
      // Check frontend port to determine if dev (3000) or prod (80)
      if (host.startsWith('100.')) {
        const frontendPort = window.location.port;
        const backendPort = (frontendPort === '3000') ? DEV_BACKEND_PORT : (frontendPort === '3010') ? 7010 : (frontendPort === '3020') ? 7020 : (frontendPort === '3030') ? 7030 : (frontendPort === '3040') ? 7040 : (frontendPort === '3050') ? 7050 : (frontendPort === '3060') ? 7060 : (frontendPort === '3070') ? 7070 : (frontendPort === '7013') ? 7003 : (frontendPort === '7015') ? 7007 : (frontendPort === '7018') ? 7008 : PROD_BACKEND_PORT;
        const apiUrl = `http://${host}:${backendPort}`;
        return apiUrl;
      }

      // For local network IPs, use same hostname with backend port
      // Check frontend port to determine if dev (3000) or prod (80)
      if (host.startsWith('192.168.') || host.startsWith('10.') || host.startsWith('172.')) {
        const frontendPort = window.location.port;
        const backendPort = (frontendPort === '3000') ? DEV_BACKEND_PORT : (frontendPort === '3010') ? 7010 : (frontendPort === '3020') ? 7020 : (frontendPort === '3030') ? 7030 : (frontendPort === '3040') ? 7040 : (frontendPort === '3050') ? 7050 : (frontendPort === '3060') ? 7060 : (frontendPort === '3070') ? 7070 : (frontendPort === '7013') ? 7003 : (frontendPort === '7015') ? 7007 : (frontendPort === '7018') ? 7008 : PROD_BACKEND_PORT;
        const apiUrl = `http://${host}:${backendPort}`;
        return apiUrl;
      }

      // For localhost access, use port-based detection (same as IP paths)
      // Do NOT use REACT_APP_API_URL here — in prod builds it points to the
      // external domain (api.debono.ai), which breaks local/headless access.
      if (host === 'localhost' || host === '127.0.0.1') {
        const frontendPort = window.location.port;
        const backendPort = (frontendPort === '3000') ? DEV_BACKEND_PORT : (frontendPort === '3010') ? 7010 : (frontendPort === '3020') ? 7020 : (frontendPort === '3030') ? 7030 : (frontendPort === '3040') ? 7040 : (frontendPort === '3050') ? 7050 : (frontendPort === '3060') ? 7060 : (frontendPort === '3070') ? 7070 : (frontendPort === '7013') ? 7003 : (frontendPort === '7015') ? 7007 : (frontendPort === '7018') ? 7008 : PROD_BACKEND_PORT;
        return `http://${host}:${backendPort}`;
      }
      
      // For debono.ai domain (including www and subdomains), use production API
      // But if we're on the same domain, try same-origin first, then fallback to api subdomain
      // FIX: Removed overly broad host.includes('debono') check which could match unrelated domains
      if (host === 'debono.ai' || host.endsWith('.debono.ai')) {
        // If already on api subdomain, use it directly
        if (host === 'api.debono.ai' || host.startsWith('api.')) {
          return `https://${host}`;
        }
        // Kyle dev port on debono.ai brand alias — same-origin (Caddy on
        // phillyshell proxies /api/* + /health to the dev backend).
        if (host === 'kyle-dev.debono.ai') {
          return `https://${host}`;
        }
        return PRODUCTION_API;
      }

      // moth.stokasoftware.com = jon's project hub. His Debono dev env is
      // path-mounted at /debono, and nginx routes /debono/api/* to HIS OWN
      // backend (:7070). Request paths already carry the /api prefix, so the
      // base must end at /debono and must NOT repeat /api.
      if (host === 'moth.stokasoftware.com') {
        return `https://${host}/debono`;
      }

      // For debono-dev.stokasoftware.com (jon's ISOLATED partner dev env), use
      // same-origin. nginx on edward proxies /api/* and /health to jon's OWN
      // backend (:7070) behind the Stoka wallet gate. Without this branch the
      // host falls through to PRODUCTION_API and jon's dev frontend would call
      // PRODUCTION debono.ai -- never allow that from an isolated dev surface.
      if (host === 'debono-dev.stokasoftware.com') {
        return `https://${host}`;
      }

      // For debono-dev.phillyshell.cloud (Tailnet-gated dev portal), use
      // same-origin — Caddy proxies /api/* and /health to the dev backend,
      // and request paths already include the /api prefix, so the base URL
      // must NOT repeat it.
      if (host === 'debono-dev.phillyshell.cloud') {
        return `https://${host}`;
      }

      if (host === 'debono-dev2.phillyshell.cloud') {
        return `https://${host}`;
      }

      if (host === 'debono-dev3.phillyshell.cloud') {
        return `https://${host}`;
      }

      if (host === 'debono-dev4.phillyshell.cloud') {
        return `https://${host}`;
      }

      if (host === 'debono-preprod.phillyshell.cloud') {
        return `https://${host}`;
      }

      // Autonomous bug-fix port review surface (Caddy proxies /api/* + /health
      // to the bugfix backend :7007; everything else to the bugfix frontend :7015).
      // Same-origin so the HTTPS subdomain never calls a plain-HTTP backend.
      if (host === 'debono-bugfix.phillyshell.cloud') {
        return `https://${host}`;
      }

      if (host === 'kyle-dev.phillyshell.cloud') {
        return `https://${host}`;
      }

      // For phillyshell.cloud domain, use the same host with /api path
      // This supports the nginx reverse proxy setup
      if (host === 'phillyshell.cloud' || host.endsWith('.phillyshell.cloud')) {
        const envApiUrl = process.env.REACT_APP_API_URL;
        if (envApiUrl) {
          return envApiUrl;
        }
        // Default to HTTPS with /api path
        const apiUrl = `https://${host}/api`;
        return apiUrl;
      }

      // Bare-hostname dev access (e.g. http://edward:3050, http://corner0:3060).
      // When the URL has no dot in the hostname AND the frontend port is in the
      // dev range (3000-3070), treat it as IP-style dev access: map to the
      // corresponding backend port on the same host. Without this branch, partner
      // devs accessing via tailnet short-name fall through to PRODUCTION_API,
      // which silently routes their dev calls to PROD and CORS-fails noisily.
      if (!host.includes('.')) {
        const frontendPort = window.location.port;
        const devPortMap = { '3000': DEV_BACKEND_PORT, '3010': 7010, '3020': 7020, '3030': 7030, '3040': 7040, '3050': 7050, '3060': 7060, '3070': 7070 };
        if (devPortMap[frontendPort]) {
          return `http://${host}:${devPortMap[frontendPort]}`;
        }
      }

      // For any other domain, try to use api subdomain
      return PRODUCTION_API;
    }
  } catch (error) {
    console.warn('[API Client] Error detecting URL:', error);
  }
  
  // Default fallback for SSR or unknown environment
  return PRODUCTION_API;
};

const API_URL = getApiUrl();
export const getApiBaseUrl = () => API_URL;

// Module-level ephemeral run holder. EphemeralRunContext provider sets
// this on mount/launch + clears on unmount/forfeit. The request
// interceptor reads it and stamps every outbound request with the
// X-Ephemeral-Run-Id header so backend writes get routed to ephemeral
// tables. Module-scoped (not React state) so the interceptor — which
// runs outside React's commit phase — sees the value synchronously.
let ephemeralRunIdRef = null;
export const setEphemeralRunId = (runId) => {
  ephemeralRunIdRef = runId || null;
};
export const getEphemeralRunId = () => ephemeralRunIdRef;

const apiClient = axios.create({
  baseURL: API_URL,
  // Don't pin a global Content-Type; let axios/browser set it.
  // (Multipart uploads especially must include a boundary.)
  timeout: 60000, // 60 second timeout (increased to prevent login failures on slow connections)
  withCredentials: true, // Send cookies (refresh_token) with requests
});

// Force timeout value (in case of caching issues)
// Deduplication for concurrent token refresh attempts.
// When multiple requests get 401 simultaneously, only one refresh call is made;
// the others wait for its result. Prevents refresh token rotation race conditions.
let _refreshPromise = null;
// Dedup concurrent anon-demo re-mints (same pattern as _refreshPromise): if the
// throwaway anon user behind drug_lab_anon_token gets reaped server-side, many
// requests 401 at once — only mint one fresh anon session and let the rest reuse it.
let _anonRemintPromise = null;
apiClient.defaults.timeout = 60000;

/**
 * Get the active replay session from sessionStorage
 * Returns null if no session or if session appears expired
 */
const getReplaySession = () => {
  try {
    const stored = sessionStorage.getItem('replay_session');
    if (stored) {
      const session = JSON.parse(stored);
      
      // Validate session has required fields
      if (!session?.session_id) {
        sessionStorage.removeItem('replay_session');
        return null;
      }
      
      // Check if session might be expired (client-side check)
      // Sessions expire after 30 minutes
      if (session.created_at) {
        const createdAt = new Date(session.created_at);
        const now = new Date();
        const minutesElapsed = (now - createdAt) / (1000 * 60);
        const expiresIn = session.expires_in_minutes || 30;
        
        if (minutesElapsed >= expiresIn) {
          sessionStorage.removeItem('replay_session');
          return null;
        }
      }
      
      return session;
    }
  } catch (e) {
    // Invalid JSON or other error, clear it
    sessionStorage.removeItem('replay_session');
  }
  return null;
};

/**
 * Check if a URL should be redirected to replay endpoints
 */
const getReplayRedirectUrl = (url, replaySession) => {
  if (!replaySession?.session_id) return null;
  
  const sessionId = replaySession.session_id;
  
  // Redirect deck list to replay endpoint
  if (url === '/api/decks/' || url === '/api/decks') {
    return `/api/decks/replay/${sessionId}/decks`;
  }
  
  // Redirect drug list to replay endpoint
  if (url === '/api/drugs/' || url === '/api/drugs') {
    return `/api/decks/replay/${sessionId}/drugs`;
  }
  
  // Redirect specific deck requests to replay endpoint
  // Note: For deck details, the frontend can use the replay session data directly
  // but we'll let the backend handle it for consistency
  
  return null;
};

// Build SHA of THIS running bundle, baked into <meta name="build-sha"> at build time.
// Sent as X-Client-Build on every request so the backend can measure build-drift across
// live clients (services/client_build_telemetry.py). The meta tag never changes within a
// page load, so we read it once and cache; a cache-bust reload is a fresh page load.
let _clientBuildSha = null;
function getClientBuildSha() {
  if (_clientBuildSha !== null) return _clientBuildSha;
  try {
    _clientBuildSha =
      (typeof document !== 'undefined' &&
        document.querySelector('meta[name="build-sha"]')?.getAttribute('content')) || '';
  } catch (_) {
    _clientBuildSha = '';
  }
  return _clientBuildSha;
}

// Request interceptor: attach Bearer token, user mode, and handle replay mode redirects
apiClient.interceptors.request.use(
  (config) => {
    // Tutorial test mode uses sessionStorage (tab-isolated) so admin's real session is untouched
    const token = getAuthToken();
    if (token) {
      try {
        if (config.headers?.set) {
          if (!config.headers.get('Authorization')) {
            config.headers.set('Authorization', `Bearer ${token}`);
          }
        } else {
          config.headers = config.headers || {};
          if (!config.headers.Authorization && !config.headers.authorization) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      } catch (_) {
        // ignore header manipulation issues
      }
    }

    // Mode rip 2026-06-13 (Phase 1): the admin/base X-User-Mode header was
    // removed. The backend no longer keys identity or deck visibility off it
    // (auth_utils._get_effective_user_id always returns the real id; the deck
    // mode-filter is a no-op).

    // X-Ephemeral-Run-Id — attached on every request when the user is
    // inside an active Reckoning run. Backend dependency reads + validates
    // this and routes level-progress writes to ephemeral tables instead
    // of the source deck's level_progress.
    const ephemeralRunId = ephemeralRunIdRef;
    if (ephemeralRunId) {
      try {
        if (config.headers?.set) {
          config.headers.set('X-Ephemeral-Run-Id', ephemeralRunId);
        } else {
          config.headers = config.headers || {};
          config.headers['X-Ephemeral-Run-Id'] = ephemeralRunId;
        }
      } catch (_) {
        // ignore header manipulation issues
      }
    }

    // X-Client-Build — the build SHA of this running bundle, on every request, so the
    // backend can observe how many live clients are behind the served build. Best-effort;
    // a header failure never blocks the request.
    const clientBuild = getClientBuildSha();
    if (clientBuild) {
      try {
        if (config.headers?.set) {
          config.headers.set('X-Client-Build', clientBuild);
        } else {
          config.headers = config.headers || {};
          config.headers['X-Client-Build'] = clientBuild;
        }
      } catch (_) {
        // ignore header manipulation issues
      }
    }

    // Handle replay mode URL redirection for GET requests only
    // (We don't want to redirect uploads or mutations)
    if (config.method === 'get') {
      const replaySession = getReplaySession();
      if (replaySession) {
        const redirectUrl = getReplayRedirectUrl(config.url, replaySession);
        if (redirectUrl) {
          config.url = redirectUrl;
        }
      }
    }

    // Remove Content-Type header for FormData (let browser set it with boundary)
    if (config.data instanceof FormData) {
      if (config.headers?.delete) {
        config.headers.delete('Content-Type');
      }
      delete config.headers?.['Content-Type'];
    } else {
      // Ensure JSON requests have a content type if not already set
      if (config.headers?.set) {
        if (!config.headers.get('Content-Type')) {
          config.headers.set('Content-Type', 'application/json');
        }
      } else if (config.headers && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Fire the progress bus on any mastery-fanout endpoint success so
    // concept-card carousels + objective grids can refetch live.
    try {
      const url = response?.config?.url || '';
      // Lazy-require to avoid a circular import chain at module init.
      // eslint-disable-next-line global-require
      const bus = require('../utils/progressBus');
      if (bus.urlTriggersProgressBus(url)) {
        bus.notifyProgressBus();
      }
    } catch {
      /* progress-bus failure must not break the response path */
    }
    return response;
  },
  async (error) => {
    recordFailedRequest(error.config, error);

    // Log errors in development for debugging
    if (process.env.NODE_ENV === 'development') {
      if (error.code === 'ECONNABORTED') {
        console.error('[API Client] Request timeout:', error.config?.url, 'Timeout was:', error.config?.timeout || apiClient.defaults.timeout, 'ms');
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        console.error('[API Client] Network error - unable to reach API:', API_URL, error);
      } else if (error.response) {
        // Expected 503s for deprecated features (e.g. master_drug_cards) — demote to warn
        const detail = error.response.data?.detail;
        const detailStr = typeof detail === 'string' ? detail : '';
        const isExpected503 = error.response.status === 503 && /table is not available/i.test(detailStr);
        const log = isExpected503 ? console.warn : console.error;
        log('[API Client] API error:', error.response.status, error.response.data);
      } else {
        console.error('[API Client] Request error:', error);
      }
    }

    // Handle 402 Payment Required (usage limit exceeded)
    if (error?.response?.status === 402) {
      const errorData = error.response.data?.detail || error.response.data || {};

      // Dispatch custom event for LimitReachedModal
      window.dispatchEvent(new CustomEvent('show-limit-modal', {
        detail: {
          message: errorData.message || 'You have reached your usage limit.',
          limitType: errorData.limit_type || 'unknown',
          currentUsage: errorData.current_usage || 0,
          limit: errorData.limit || 0,
          upgradeUrl: errorData.upgrade_url || '/dashboard'
        }
      }));

      // Mark as handled so components can check error.handled
      const handledError = new Error(errorData.message || 'Usage limit exceeded');
      handledError.handled = true;
      handledError.limitExceeded = true;
      handledError.response = error.response;
      return Promise.reject(handledError);
    }

    // Handle 503 READ_ONLY_FAILOVER (secondary node blocking writes)
    if (error?.response?.status === 503 && error?.response?.data?.error_code === 'READ_ONLY_FAILOVER') {
      const failoverData = error.response.data;
      window.dispatchEvent(new CustomEvent('show-read-only-modal', {
        detail: {
          message: failoverData.detail || 'This server is in read-only mode.',
          nodeRole: failoverData.node_role,
          servingState: failoverData.serving_state,
        }
      }));

      const handledError = new Error(failoverData.detail);
      handledError.handled = true;
      handledError.readOnlyFailover = true;
      handledError.response = error.response;
      return Promise.reject(handledError);
    }

    // If a replay endpoint returns 404, the session is expired/invalid - clear it
    const url = error.config?.url || '';
    if (error?.response?.status === 404 && url.includes('/api/decks/replay/')) {
      try {
        sessionStorage.removeItem('replay_session');
      } catch (_) {
        // ignore
      }
      // Reload the page to get fresh data
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }

    // Anon-demo self-heal: if the request was authed with the throwaway anon
    // token (lowest precedence in getAuthToken) and the backend 401s — typically
    // because the anon user was reaped server-side (the 7-day janitor / manual
    // wipe) or the short-lived demo token expired (2h for /play/:slug) and
    // get_current_user now returns "Could not validate credentials" — mint a FRESH
    // anon session and retry once. This keeps the no-signup landing playable without
    // the visitor having to manually clear localStorage.
    //
    // Inside an HTN /play/:slug demo, the fresh mint is scoped with demo_slug (an
    // empty body would create a GENERIC anon user with a random loadout, not the
    // curated HTN region), and the run's persisted regimen is replayed onto the new
    // account BEFORE the failed request retries — so server-derived combat sees the
    // same regimen the map already remembers. The replay is folded into the deduped
    // _anonRemintPromise so concurrent 401s all retry against one restored account.
    //
    // 2026-07-27: we now hand the EXPIRING token back as `prior_token`. The two causes
    // of this 401 need opposite handling and used to be conflated: a REAPED user really
    // is gone and must be replaced, but an EXPIRED token belongs to an account that is
    // still alive and still owns the run. The server verifies that token's SIGNATURE
    // (ignoring expiry) and refreshes the SAME user_id when it can, so an in-flight
    // combat session is no longer orphaned into permanent 403s. It falls back to a
    // fresh mint whenever the proof does not hold, so a reaped user still self-heals.
    if (
      error?.response?.status === 401 &&
      !error?.config?._anonRetry &&
      !error?.config?._isRefreshAttempt &&
      !error?.config?._isAnonMint &&
      getDrugLabAnonToken() &&
      getAuthToken() === getDrugLabAnonToken()
    ) {
      try {
        const htnDemoSlug = htnDemoSlugFromPath();
        if (!_anonRemintPromise) {
          // Capture BEFORE clearing — this token is the only proof we ever held the
          // identity, and clearing it first is what made the old behaviour lossy.
          const priorAnonToken = getDrugLabAnonToken();
          clearDrugLabAnonToken();
          _anonRemintPromise = (async () => {
            const mintBody = htnDemoSlug ? { demo_slug: htnDemoSlug } : {};
            if (priorAnonToken) mintBody.prior_token = priorAnonToken;
            const remint = await axios.post(
              `${API_URL}/api/auth/anon-demo`, mintBody, { _isAnonMint: true },
            );
            const mintedToken = remint?.data?.access_token;
            // Replay the persisted regimen onto the fresh account. Raw axios (not
            // apiClient) so these never recurse through this interceptor; sequential
            // for deterministic slot allocation; best-effort per slug (idempotent
            // server-side). Combat re-derives from the resulting equipment_state.
            // Skipped on a re-issue: the identity did not change, so its equipment_state
            // is already correct and replaying would only churn slots.
            if (mintedToken && htnDemoSlug && !remint?.data?.reissued) {
              const savedSlugs = readHtnEquipmentSlugs(htnDemoSlug);
              for (const slug of savedSlugs) {
                try {
                  await axios.post(
                    `${API_URL}/api/htn/regimen/equip`, { slug },
                    { _isAnonMint: true, headers: { Authorization: `Bearer ${mintedToken}` } },
                  );
                } catch (_) { /* best-effort — combat re-derives */ }
              }
            }
            return remint;
          })().finally(() => { _anonRemintPromise = null; });
        }
        const remint = await _anonRemintPromise;
        const newAnon = remint?.data?.access_token;
        if (newAnon) {
          setDrugLabAnonToken(newAnon);
          error.config._anonRetry = true;
          error.config.headers = error.config.headers || {};
          if (typeof error.config.headers.set === 'function') {
            error.config.headers.set('Authorization', `Bearer ${newAnon}`);
          } else {
            error.config.headers.Authorization = `Bearer ${newAnon}`;
          }
          return apiClient.request(error.config);
        }
      } catch (_anonErr) {
        // fall through to normal 401 handling
      }
    }

    if (error?.response?.status === 401 && !error?.config?._skipAuthRedirect && !error?.config?._isRefreshAttempt) {
      // Anon-demo isolation: never refresh the real session nor redirect to
      // /login from an isolated /play demo tab. The public-path check below
      // already covers /play, but flag-gating is robust even if that ever drifts
      // — and it guarantees we never call setAccessToken() (which would release
      // isolation) on the visitor's behalf.
      if (isAnonDemoOnly()) {
        return Promise.reject(error);
      }
      // Never redirect away from public pages.
      if (typeof window !== 'undefined' && isPublicPath(window.location?.pathname)) {
        return Promise.reject(error);
      }
      // Try to refresh the token before redirecting to login.
      // Deduplicate: if a refresh is already in flight (e.g., multiple 401s from
      // concurrent requests during a long CRAM session), reuse its promise so only
      // one refresh call hits the backend. This prevents refresh token rotation races
      // where the second call sends the already-rotated (now invalid) cookie.
      // Refresh outcome gate. Only a DEFINITIVE rejection logs the user out.
      let refreshFatal = false;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios.post(`${API_URL}/api/auth/refresh`, null, {
            withCredentials: true,
            _isRefreshAttempt: true,
          }).finally(() => { _refreshPromise = null; });
        }
        const refreshResp = await _refreshPromise;
        const newToken = refreshResp.data?.access_token;
        if (newToken) {
          setAccessToken(newToken);
          // Retry the original request with the new token
          error.config.headers.Authorization = `Bearer ${newToken}`;
          error.config._skipAuthRedirect = true;
          return apiClient.request(error.config);
        }
        // 2xx but no token = a malformed/dead refresh response → the session
        // really is gone. Treat as fatal.
        refreshFatal = true;
      } catch (_refreshErr) {
        // ONLY a definitive auth rejection (401/403) means the refresh token is
        // truly invalid/expired and the user must re-authenticate. A network
        // error, timeout, 5xx, or 429 is TRANSIENT — the session may be perfectly
        // valid — so we must NOT clear the token or redirect. A blip (Tailscale
        // hiccup, a slow call during a long combat) must never masquerade as a
        // logout; the next request retries refresh and self-heals.
        const rs = _refreshErr?.response?.status;
        refreshFatal = rs === 401 || rs === 403;
      }

      if (!refreshFatal) {
        // Transient refresh failure: keep the session intact, just fail this call.
        return Promise.reject(error);
      }

      // Definitive dead session below → sign out.
      // In tutorial test mode, don't touch localStorage or redirect — just close the tab
      if (hasTutorialTestToken()) {
        clearTutorialTestToken();
        return Promise.reject(error);
      }

      clearAccessToken();
      clearActiveMode();
      clearPrivateSessionStorage();
      try {
        window.dispatchEvent(new Event(AUTH_FORCED_SIGN_OUT_EVENT));
      } catch (_) {
        // ignore
      }
      // Force navigation to login (client-side router-safe), preserving where the
      // user was so re-login returns them there instead of dumping them on /login.
      try {
        if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
          const here = `${window.location.pathname || ''}${window.location.search || ''}`;
          window.location.href = (here && here !== '/')
            ? `/login?next=${encodeURIComponent(here)}`
            : '/login';
        }
      } catch (_) {
        // ignore
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
